/** Translates the subset of registered REST error codes the UI can act on. */
const API_ERROR_TRANSLATION_KEYS = {
    gateway_offline: 'errors.gateway_offline',
    permission_denied: 'errors.permission_denied',
    not_authenticated: 'errors.not_authenticated',
    pending_token_not_permitted: 'errors.pending_token_not_permitted',
    validation_error: 'errors.validation_error',
    conflict: 'errors.conflict',
    // The refusals the front desk meets (GRMS §11, cards, locks). Each is a
    // sentence of its own; the server's English `message` never reaches the desk.
    room_state_transition_rejected: 'errors.room_state_transition_rejected',
    dnd_active: 'errors.dnd_active',
    room_move_conflict: 'errors.room_move_conflict',
    room_not_found: 'errors.room_not_found',
    flag_channel_unavailable: 'errors.flag_channel_unavailable',
    card_not_found: 'errors.card_not_found',
    card_type_forbidden: 'errors.card_type_forbidden',
    card_uid_in_use: 'errors.card_uid_in_use',
    card_retired: 'errors.card_retired',
    card_not_mutable: 'errors.card_not_mutable',
    carrier_not_permitted: 'errors.carrier_not_permitted',
    claim_id_missing: 'errors.claim_id_missing',
    lock_not_found: 'errors.lock_not_found',
    device_offline: 'errors.device_offline',
};
/**
 * `details.reason` values that tell one `resource_state_conflict` from another
 * (BMS-spec rest-api.md §Hotel-relative thermostat bindings). A known reason wins over the code.
 */
const API_ERROR_REASON_KEYS = {
    inventory_pending: 'errors.inventory_pending',
    zone_not_bound: 'errors.hotelThermostat.zone_not_bound',
    no_confirmed_policy: 'errors.hotelThermostat.no_confirmed_policy',
    zone_bound_elsewhere: 'errors.hotelThermostat.zone_bound_elsewhere',
    not_acknowledgeable: 'errors.hotelThermostat.not_acknowledgeable',
    no_device_policy: 'errors.hotelThermostat.no_device_policy',
    stay_reset_owed: 'errors.hotelThermostat.stay_reset_owed',
    not_hotel_thermostat: 'errors.hotelThermostat.not_hotel_thermostat',
};
function getApiErrorEnvelope(err) {
    if (!err || typeof err !== 'object')
        return undefined;
    const data = err.response?.data;
    if (!data || typeof data !== 'object')
        return undefined;
    return data;
}
/**
 * A refusal the server marks as transient (`details.retryable`), e.g. a hotel thermostat whose
 * inventory is still catching up with a config change: not a fault, the same command works shortly.
 */
export function isRetryableApiError(err) {
    const details = getApiErrorEnvelope(err)?.details;
    return !!details && typeof details === 'object' && details.retryable === true;
}
/** A translated sentence for a failure whose code the UI does not know. */
function genericApiError(err, t) {
    const response = err?.response;
    if (!response)
        return t(err && typeof err === 'object' && 'isAxiosError' in err ? 'errors.networkError' : 'errors.unexpected');
    const status = typeof response.status === 'number' ? response.status : 0;
    if (status === 401 || status === 403)
        return t('errors.permission_denied');
    if (status === 400 || status === 422)
        return t('errors.validation_error');
    if (status === 404)
        return t('errors.notFound');
    if (status === 409)
        return t('errors.conflict');
    if (status >= 500)
        return t('errors.serverError');
    return t('errors.unexpected');
}
/**
 * Whether `err` came from a REST request (an Axios error, with or without an
 * answer) — as opposed to a local failure such as the NFC agent's, whose own
 * text the caller may show.
 */
export function isApiRequestError(err) {
    if (!err || typeof err !== 'object')
        return false;
    return 'response' in err || ('isAxiosError' in err && err.isAxiosError === true);
}
export function formatApiError(err, t, options = {}) {
    const envelope = getApiErrorEnvelope(err);
    if (!envelope)
        return options.serverMessage === false ? genericApiError(err, t) : t('errors.unexpected');
    // `resource_state_conflict` is shared by many refusals; the machine-readable reason tells this one apart.
    const reason = envelope.details?.reason;
    if (typeof reason === 'string' && API_ERROR_REASON_KEYS[reason]) {
        return t(API_ERROR_REASON_KEYS[reason]);
    }
    if (typeof envelope.error === 'string') {
        const translationKey = API_ERROR_TRANSLATION_KEYS[envelope.error];
        if (translationKey)
            return t(translationKey);
    }
    if (options.serverMessage === false)
        return genericApiError(err, t);
    if (typeof envelope.message === 'string' && envelope.message.trim()) {
        return envelope.message;
    }
    return t('errors.unexpected');
}
/**
 * `not_authenticated` is one wire code for several server-side reasons — an
 * expired session, an unknown refresh token, a bad username/password — each
 * carrying its own `message`. `formatApiError`'s generic mapping picks the
 * "your session has expired" translation for all of them, which is right for
 * an authenticated request that suddenly 401s and wrong for a first login
 * attempt, where there was never a session to expire. `POST /api/auth/login`
 * always sends the specific reason as `message` (e.g. "Invalid username or
 * password"); use that verbatim instead of the generic mapping. Any other
 * error code (network, validation, …) still goes through `formatApiError`.
 */
export function formatLoginError(err, t) {
    const envelope = getApiErrorEnvelope(err);
    if (envelope?.error === 'not_authenticated' && typeof envelope.message === 'string' && envelope.message.trim()) {
        return envelope.message;
    }
    return formatApiError(err, t);
}
//# sourceMappingURL=apiError.js.map